/**
 * Archive/unarchive dialogs for a member. Archiving always cascades to the
 * member's patients; unarchiving un-cascades any patients that were cascaded
 * during the original archive.
 */
import { useState } from 'react'
import { toast } from 'sonner'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/frontend/components/ui/alert-dialog'
import { Button } from '@/frontend/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/frontend/components/ui/dialog'
import { Label } from '@/frontend/components/ui/label'
import { Textarea } from '@/frontend/components/ui/textarea'
import { useArchiveMember } from '@/frontend/hooks/api/use-archive-member'
import { useUnarchiveMember } from '@/frontend/hooks/api/use-unarchive-member'

interface ArchiveMemberDialogProps {
  memberId: string
  archivedAt: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** Renders the archive or unarchive variant based on `archivedAt`. */
export function ArchiveMemberDialog({
  memberId,
  archivedAt,
  open,
  onOpenChange,
}: ArchiveMemberDialogProps) {
  if (archivedAt) {
    return <UnarchiveDialog memberId={memberId} open={open} onOpenChange={onOpenChange} />
  }
  return <ArchiveDialog memberId={memberId} open={open} onOpenChange={onOpenChange} />
}

function ArchiveDialog({
  memberId,
  open,
  onOpenChange,
}: {
  memberId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [reason, setReason] = useState('')
  const archive = useArchiveMember(memberId)

  const trimmed = reason.trim()
  const canSubmit = trimmed.length > 0 && !archive.isPending

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    archive.mutate(
      { reason: trimmed },
      {
        onSuccess: () => {
          toast.success('Member archived')
          onOpenChange(false)
          setReason('')
        },
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : 'Failed to archive member')
        },
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Archive member</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-muted-foreground">
            Archiving keeps the member's history but marks them as inactive, and archives every
            active patient they own with the reason "Owner was archived". Provide a reason so the
            team has context.
          </p>
          <div className="space-y-2">
            <Label htmlFor="archive-reason">Reason</Label>
            <Textarea
              id="archive-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Duplicate account, member has left the practice..."
              maxLength={500}
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={archive.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {archive.isPending ? 'Archiving…' : 'Archive'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function UnarchiveDialog({
  memberId,
  open,
  onOpenChange,
}: {
  memberId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const unarchive = useUnarchiveMember(memberId)

  function handleConfirm() {
    unarchive.mutate(undefined, {
      onSuccess: () => {
        toast.success('Member restored')
        onOpenChange(false)
      },
      onError: (err) => {
        toast.error(err instanceof Error ? err.message : 'Failed to restore member')
      },
    })
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Unarchive member?</AlertDialogTitle>
          <AlertDialogDescription>
            This will restore the member and any patients that were archived automatically as part
            of the original archive. Patients that were archived for other reasons stay archived.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={unarchive.isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm} disabled={unarchive.isPending}>
            {unarchive.isPending ? 'Restoring…' : 'Unarchive'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
